import { authApi } from './api';

export interface TokenInfo {
  isValid: boolean;
  expiresAt: number;
  timeUntilExpiry: number;
  needsRefresh: boolean;
}

class TokenService {
  private refreshInterval: NodeJS.Timeout | null = null;
  private readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
  private readonly CHECK_INTERVAL = 60 * 1000; // Check every minute

  /**
   * Parse JWT token and extract expiration info
   */
  getTokenInfo(token?: string): TokenInfo | null {
    const authToken = token || localStorage.getItem('token');
    
    if (!authToken) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expiresAt - currentTime;
      const needsRefresh = timeUntilExpiry <= this.REFRESH_THRESHOLD;
      const isValid = timeUntilExpiry > 0;

      return {
        isValid,
        expiresAt,
        timeUntilExpiry,
        needsRefresh
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return {
        isValid: false,
        expiresAt: 0,
        timeUntilExpiry: 0,
        needsRefresh: true
      };
    }
  }

  /**
   * Format time until expiry in human readable format
   */
  formatTimeUntilExpiry(timeUntilExpiry: number): string {
    if (timeUntilExpiry <= 0) {
      return 'Expired';
    }

    const minutes = Math.floor(timeUntilExpiry / (60 * 1000));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Start automatic token refresh monitoring
   */
  startTokenMonitoring(): void {
    // Clear any existing interval
    this.stopTokenMonitoring();

    this.refreshInterval = setInterval(async () => {
      const tokenInfo = this.getTokenInfo();
      
      if (!tokenInfo) {
        console.log('No token found, stopping monitoring');
        this.stopTokenMonitoring();
        return;
      }

      if (!tokenInfo.isValid) {
        console.log('Token expired, redirecting to login');
        this.handleTokenExpired();
        return;
      }

      if (tokenInfo.needsRefresh) {
        console.log(`Token expires in ${this.formatTimeUntilExpiry(tokenInfo.timeUntilExpiry)}, refreshing...`);
        try {
          await this.refreshToken();
          console.log('Token refreshed successfully');
        } catch (error) {
          console.error('Failed to refresh token:', error);
          this.handleTokenExpired();
        }
      }
    }, this.CHECK_INTERVAL);

    console.log('Token monitoring started');
  }

  /**
   * Stop automatic token refresh monitoring
   */
  stopTokenMonitoring(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('Token monitoring stopped');
    }
  }

  /**
   * Manually refresh the token
   */
  async refreshToken(): Promise<void> {
    try {
      await authApi.refreshToken();
      
      // Dispatch custom event to notify components
      window.dispatchEvent(new CustomEvent('tokenRefreshed', {
        detail: { timestamp: Date.now() }
      }));
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Handle token expiration
   */
  private handleTokenExpired(): void {
    this.stopTokenMonitoring();
    
    // Clear all stored data
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Dispatch custom event to notify components
    window.dispatchEvent(new CustomEvent('tokenExpired', {
      detail: { timestamp: Date.now() }
    }));
    
    // Redirect to login
    window.location.href = '/login';
  }

  /**
   * Check if refresh token is valid
   */
  isRefreshTokenValid(): boolean {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const currentTime = Date.now();
      return expiresAt > currentTime;
    } catch (error) {
      console.error('Error parsing refresh token:', error);
      return false;
    }
  }

  /**
   * Get time until refresh token expires
   */
  getRefreshTokenInfo(): TokenInfo | null {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiresAt - currentTime;
      const isValid = timeUntilExpiry > 0;

      return {
        isValid,
        expiresAt,
        timeUntilExpiry,
        needsRefresh: false // Refresh tokens don't need refreshing
      };
    } catch (error) {
      console.error('Error parsing refresh token:', error);
      return {
        isValid: false,
        expiresAt: 0,
        timeUntilExpiry: 0,
        needsRefresh: false
      };
    }
  }
}

// Export singleton instance
export const tokenService = new TokenService();
export default tokenService;
